import { useEffect, useState } from "react";
import { MailCheck, RefreshCw, LogOut, Check } from "lucide-react";
import FinPipMark from "./FinPipMark";
import { authApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";

/**
 * Shown after registration when the account exists but the email hasn't been
 * confirmed yet (gated on the `email_verified` flag from /api/me). The user
 * must click the link we emailed; this screen lets them resend it, re-check
 * (the verification link redirects back with ?verified=1), or sign out.
 */
export default function VerifyEmailPending() {
  const { t } = useI18n();
  const { user, refresh, signOut } = useAuth();
  const [resending, setResending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The verify link bounces the browser back to /journal/?verified=1 — pick
  // that up, refresh auth (which unlocks the app), and tidy the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("verified") || params.has("verify_error")) {
      if (params.has("verify_error")) {
        setError(t("verify.linkError"));
      }
      window.history.replaceState({}, "", window.location.pathname);
      void refresh();
    }
  }, [refresh]);

  async function resend() {
    setResending(true);
    setError(null);
    setSent(false);
    try {
      await authApi.resendVerification();
      setSent(true);
    } catch (err) {
      setError((err as Error).message || t("verify.resendError"));
    } finally {
      setResending(false);
    }
  }

  async function recheck() {
    setChecking(true);
    setError(null);
    try {
      await refresh();
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <FinPipMark className="w-10 h-10 text-[#0c34f4] dark:text-white" />
          <div>
            <p className="text-xl font-bold text-parchment tracking-tight">FinPip Journal</p>
            <p className="text-parchment-muted text-xs">{t("verify.subtitle")}</p>
          </div>
        </div>

        <section className="glass-gold p-6 sm:p-8">
          <div className="mb-5 flex items-center gap-2 text-parchment">
            <MailCheck size={20} className="text-gold-400" />
            <h1 className="text-lg font-semibold">{t("verify.title")}</h1>
          </div>

          <p className="text-sm text-parchment-muted">
            {t("verify.sentTo1")}
            <span className="font-semibold text-parchment">{user?.email}</span>{t("verify.sentTo2")}
          </p>
          <p className="mt-2 text-xs text-parchment-muted">
            {t("verify.spam")}
          </p>

          {sent && (
            <p className="mt-4 flex items-center gap-1.5 text-sm text-emerald-400">
              <Check size={16} /> {t("verify.sent")}
            </p>
          )}
          {error && <p className="mt-4 text-sm text-red-400">{error}</p>}

          <div className="mt-6 grid gap-3">
            <button
              type="button"
              onClick={() => void recheck()}
              disabled={checking}
              className="btn-gold w-full disabled:opacity-50"
            >
              <RefreshCw className={"w-4 h-4" + (checking ? " animate-spin" : "")} />
              {checking ? t("verify.checking") : t("verify.confirmed")}
            </button>
            <button
              type="button"
              onClick={() => void resend()}
              disabled={resending}
              className="btn-ghost w-full disabled:opacity-50"
            >
              <MailCheck className="w-4 h-4" />
              {resending ? t("verify.sending") : t("verify.resend")}
            </button>
          </div>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => void signOut()}
              className="inline-flex items-center gap-1.5 text-sm text-parchment-muted hover:text-parchment"
            >
              <LogOut className="w-4 h-4" /> {t("nav.signOut")}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
